import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  getMultilingualString,
  getResolvedProductSellingConfig,
} from "shared/utils";
import {
  CollapsableStep,
  QuestionnaireQuestionList,
} from "widgets/booker/shared";
import { useBookerContext, useCartContext } from "widgets/booker/contexts";
import { useSurveyByIdQuery } from "gql/generated";
import {
  areRequiredDynamicFeeQuestionsAnswered,
  getActiveDynamicFeeQuestions,
  getDefaultResponseForQuestion,
  getDynamicFeeSurveyId,
  getPricingConditionQuestionIds,
  mergeDynamicFeeResponses,
} from "./dynamicFeeQuestionnaireUtils";

export const DynamicFeeQuestionnaire = () => {
  const { product, booker } = useBookerContext();
  const {
    questionnaireResponses,
    updateQuestionnaireResponses,
    questionnaireComplete,
    setQuestionnaireCompleteState,
  } = useCartContext();
  const { t } = useTranslation("booker", { keyPrefix: "QUESTIONNAIRE" });

  const dynamicFeeSurveyId = getDynamicFeeSurveyId(product as any);

  const { data, loading } = useSurveyByIdQuery({
    variables: { id: dynamicFeeSurveyId ?? "" },
    skip: !dynamicFeeSurveyId,
  });

  const survey = data?.surveyById;
  const pricingQuestionIds = useMemo(
    () =>
      getPricingConditionQuestionIds(
        getResolvedProductSellingConfig(
          product as Parameters<typeof getResolvedProductSellingConfig>[0],
        ).options,
      ),
    [product],
  );
  const questions = useMemo(
    () => getActiveDynamicFeeQuestions(survey, pricingQuestionIds),
    [survey, pricingQuestionIds],
  );

  const isCheckoutSurvey =
    !!booker?.survey?._id &&
    !!dynamicFeeSurveyId &&
    String(booker.survey._id) === String(dynamicFeeSurveyId);
  const checkoutSurveyRequiresAnswers = !!booker?.survey?.questions.some(
    question => question.required && question.isActive,
  );

  useEffect(() => {
    if (!survey || questions.length === 0) return;

    updateQuestionnaireResponses(current => {
      const questionIds = new Set(questions.map(q => q._id));
      const hasAllResponses = questions.every(q =>
        current.some(r => r.questionData._id === q._id),
      );
      if (hasAllResponses) return current;

      return [
        ...current.filter(r => !questionIds.has(r.questionData._id)),
        ...questions.map(question => {
          const existing = current.find(
            r => r.questionData._id === question._id,
          );
          return {
            questionData: question,
            response:
              existing?.response ?? getDefaultResponseForQuestion(question),
          };
        }),
      ];
    });
  }, [questionnaireResponses, questions, survey, updateQuestionnaireResponses]);

  useEffect(() => {
    if (!isCheckoutSurvey || !checkoutSurveyRequiresAnswers || !survey) return;
    const complete = areRequiredDynamicFeeQuestionsAnswered(
      questions,
      questionnaireResponses,
    );
    if (complete !== questionnaireComplete) {
      setQuestionnaireCompleteState(complete);
    }
  }, [
    checkoutSurveyRequiresAnswers,
    isCheckoutSurvey,
    questions,
    questionnaireComplete,
    questionnaireResponses,
    setQuestionnaireCompleteState,
    survey,
  ]);

  if (!dynamicFeeSurveyId || loading || !survey || questions.length === 0) {
    return null;
  }

  const isComplete = areRequiredDynamicFeeQuestionsAnswered(
    questions,
    questionnaireResponses,
  );

  const handleResponseChange = (questionId: string, response: string[]) => {
    updateQuestionnaireResponses(current =>
      mergeDynamicFeeResponses(current, survey, questionId, response),
    );
  };

  return (
    <CollapsableStep
      title={getMultilingualString(survey.name ?? {}) || t("TITLE")}
      complete={isComplete}
      stepId="pricing-questions"
    >
      <QuestionnaireQuestionList
        questions={questions}
        getResponse={question =>
          questionnaireResponses.find(r => r.questionData._id === question._id)
            ?.response ?? getDefaultResponseForQuestion(question)
        }
        onResponseChange={handleResponseChange}
      />
    </CollapsableStep>
  );
};
